import {
  HlsMediaInfo
} from "./chunk-WTKY2MT3.js";
import {
  AbortError
} from "./chunk-U5XM3IFB.js";
import {
  canPlayType
} from "./chunk-HRUHRV5N.js";
import {
  guessMediaTypeOfUrl,
  isAudioOnlyMediaType,
  isHlsMediaType
} from "./chunk-2UU3XUBN.js";
import {
  AdapterBaseHtmlMedia,
  HtmlMediaSubtitles
} from "./chunk-TWTJHLMH.js";
import {
  convertTimeRanges,
  implementsStatic
} from "./chunk-BPZZ3HZX.js";
import {
  PlaybackSessionError
} from "./chunk-7BHS56SI.js";
import {
  getLogger
} from "./chunk-LFIBM62H.js";
import {
  isAbortError,
  toError
} from "./chunk-2GHSQBQ5.js";
import {
  __decorateClass
} from "./chunk-MVGVKF42.js";

// src/adapter/native/adapter.ts
import {
  DestroyableAbortController,
  DestroyableSet,
  destroyableEventListener as addEventListener2,
  whenDestroyed
} from "@nrk/destroyable";

// src/adapter/native/hls-subtitles.ts
import { destroyableEventListener as addEventListener } from "@nrk/destroyable";
var logger = getLogger("HlsNativeSubtitles");
var HlsNativeSubtitles = class extends HtmlMediaSubtitles {
  placeholderTextTrack;
  numMissingTracks;
  trackListenerHandle;
  constructor(element, eventEmitter, configs) {
    super(element, eventEmitter, configs);
    logger.log(`${element.textTracks.length} of ${configs.length} text tracks are present.`);
    this.numMissingTracks = configs.length - element.textTracks.length;
    if (this.numMissingTracks > 0) {
      this.trackListenerHandle = addEventListener(element.textTracks, "addtrack", this.handleTrackAdded.bind(this));
    }
  }
  supportsAddingTracks() {
    return false;
  }
  addSubtitlesTrack() {
    throw new Error("Not supported.");
  }
  getTextTrack(config) {
    if (!this.element) {
      return void 0;
    }
    const textTrackList = this.element.textTracks;
    for (let i = 0; i < textTrackList.length; ++i) {
      const track = textTrackList[i];
      if (track.kind === "subtitles" && track.label === config.label) {
        return track;
      }
    }
    if (this.numMissingTracks > 0) {
      logger.log(
        `Creating a placeholder text track (${config.label}, ${config.language}) in anticipation of the real track.`
      );
      this.placeholderTextTrack = {
        label: config.label,
        language: config.language,
        mode: "disabled",
        oncuechange: null
      };
      return this.placeholderTextTrack;
    }
    return void 0;
  }
  handleTrackAdded(event) {
    const track = event.track;
    const fakeTrack = this.placeholderTextTrack;
    if (!track || !(track instanceof TextTrack) || track.kind !== "subtitles") {
      return;
    }
    this.numMissingTracks--;
    if (this.numMissingTracks <= 0 && this.element) {
      this.trackListenerHandle?.destroy();
      this.trackListenerHandle = void 0;
      logger.log("All text tracks are now present.");
    }
    if (fakeTrack && fakeTrack.label === track.label) {
      logger.log(
        `Placeholder track (${fakeTrack.label}, ${fakeTrack.language}) replaced by real track${fakeTrack.language !== track.language ? ` (language changed to ${track.language}!)` : ""}.`
      );
      track.mode = fakeTrack.mode;
      track.oncuechange = fakeTrack.oncuechange;
      if (this.activeTrack === fakeTrack) {
        this.activeTrack = track;
      }
      this.placeholderTextTrack = void 0;
    }
  }
  destroy() {
    this.trackListenerHandle?.destroy();
    this.trackListenerHandle = void 0;
    super.destroy();
  }
};

// src/adapter/htmlmedia-range-observable.ts
import { Observable as Observable2 } from "zen-observable-ts";

// src/utils/event-target-observable.ts
import { Observable } from "zen-observable-ts";
function eventTargetObservable(target, events) {
  return new Observable((observer) => {
    const proxy = (event) => observer.next(event);
    events.forEach((event) => target.addEventListener(event, proxy));
    return () => events.forEach((event) => target.removeEventListener(event, proxy));
  });
}

// src/adapter/htmlmedia-range-observable.ts
function htmlMediaRangeObservable(mediaElement) {
  return new Observable2((observer) => {
    let previousStart;
    let previousEnd;
    const check = () => {
      const timestamp = Date.now() / 1e3;
      const duration = mediaElement.duration;
      if (Number.isNaN(duration)) {
        return;
      }
      let start;
      let end;
      if (mediaElement.seekable !== void 0 && mediaElement.seekable.length > 0) {
        start = mediaElement.seekable.start(0);
        end = mediaElement.seekable.end(0);
      } else {
        start = 0;
        end = duration;
      }
      if (Number.isFinite(duration)) {
        observer.next({ start, end, complete: true, timestamp });
        observer.complete();
      } else {
        if (start !== previousStart || end !== previousEnd) {
          previousStart = start;
          previousEnd = end;
          observer.next({ start, end, complete: false, timestamp });
        }
      }
    };
    check();
    if (!observer.closed) {
      const eventSub = eventTargetObservable(mediaElement, [
        "durationchange",
        "loadeddata",
        "loadedmetadata",
        "progress"
      ]).subscribe(() => check());
      return () => eventSub.unsubscribe();
    }
  });
}

// src/adapter/htmlvideo-quality-observable.ts
import { Observable as Observable3 } from "zen-observable-ts";
function htmlVideoQualityObservable(videoElement, options) {
  const logger3 = getLogger("VideoQualityObservable");
  return new Observable3((observer) => {
    let previousWidth;
    let previousHeight;
    let previousFrameRate;
    let previousFrameCount = 0;
    let previousFrameCountTime = 0;
    let dropNextFrameCount = true;
    const frameRates = options?.frameRates ?? [];
    frameRates.sort();
    const snapDistance = Math.floor(
      frameRates.reduce(
        (diff, frameRate, index) => index === 0 ? frameRate : Math.min(diff, (frameRates[index] - frameRates[index - 1]) / 2),
        0
      )
    );
    const frameRateDetectionEnabled = !!videoElement.getVideoPlaybackQuality && frameRates.length > 1;
    if (frameRateDetectionEnabled) {
      logger3.log(`Frame rate detection enabled. Got: ${frameRates.join(", ")}. Snap distance: ${snapDistance}.`);
    } else {
      logger3.log(`Frame rate detection disabled. ${frameRates.length > 1 ? "No support." : "Single frame rate."}`);
    }
    const check = () => {
      let qualityChanged = false;
      const width = videoElement.videoWidth;
      const height = videoElement.videoHeight;
      if (width !== previousWidth || height !== previousHeight) {
        logger3.log(`Resolution changed from ${previousWidth}x${previousHeight} to ${width}x${height}.`);
        previousWidth = width;
        previousHeight = height;
        qualityChanged = true;
        dropNextFrameCount = true;
      }
      if (frameRateDetectionEnabled) {
        const frameCounts = videoElement.getVideoPlaybackQuality();
        const frameCount = frameCounts.totalVideoFrames;
        const frameCountTime = frameCounts.creationTime;
        const frameRate = Math.round(
          (frameCount - previousFrameCount) / (frameCountTime - previousFrameCountTime) * 1e3
        );
        previousFrameCount = frameCount;
        previousFrameCountTime = frameCountTime;
        if (frameRate === 0) {
          dropNextFrameCount = true;
        } else if (dropNextFrameCount) {
          dropNextFrameCount = false;
        } else {
          for (let i = 0; i < frameRates.length; ++i) {
            if (Math.abs(frameRate - frameRates[i]) <= snapDistance) {
              if (frameRates[i] !== previousFrameRate) {
                qualityChanged = true;
                logger3.log(`Frame rate changed from ${previousFrameRate} to ${frameRates[i]}`);
                previousFrameRate = frameRates[i];
              }
              break;
            }
          }
        }
      }
      if (qualityChanged) {
        observer.next({ width: previousWidth, height: previousHeight, frameRate: previousFrameRate });
      }
    };
    let intervalId;
    const start = () => {
      if (intervalId === void 0) {
        check();
        intervalId = setInterval(check, options?.pollFrequency ?? 500);
        logger3.log("Tracking video quality change.");
      }
    };
    const stop = () => {
      if (intervalId !== void 0) {
        clearInterval(intervalId);
        intervalId = void 0;
        logger3.log("Stopped tracking video quality change.");
      }
    };
    const events = ["loadeddata", "play", "playing", "paused", "ended"];
    const eventSub = eventTargetObservable(videoElement, events).subscribe((event) => {
      if (event.type === "paused" || event.type === "ended") {
        stop();
      } else {
        start();
      }
    });
    if (!videoElement.paused) {
      start();
    }
    return () => {
      eventSub.unsubscribe();
      stop();
    };
  });
}

// src/utils/destroyable-subscribe.ts
import { DestroyableBase } from "@nrk/destroyable";
var DestroyableSubscription = class extends DestroyableBase {
  subscription;
  constructor(observable, ...args) {
    super();
    const [onNext, onError, onComplete] = args;
    this.subscription = observable.subscribe(onNext, onError, () => {
      onComplete?.();
      this.destroy();
    });
  }
  destroy() {
    this.subscription.unsubscribe();
    super.destroy();
  }
};
var subscribe = (observable, ...args) => new DestroyableSubscription(observable, ...args);

// src/adapter/native/adapter.ts
var adapterInfo = {
  id: "Native",
  name: "Native adapter",
  tech: "HTML5",
  version: "n/a"
};
var logger2 = getLogger("NativeAdapter");
var NativeAdapter = class extends AdapterBaseHtmlMedia {
  qualityTracker;
  mediaInfo;
  startTime;
  startTimeLive;
  mediaRangeObservable;
  constructor(context) {
    super(context);
    const mediaType = context.media.mediaType ?? guessMediaTypeOfUrl(context.media.url);
    if (mediaType === void 0) {
      throw new Error("Failed to resolve media type. Please specify.");
    }
    if (isHlsMediaType(mediaType)) {
      this.mediaInfo = new HlsMediaInfo(context.media);
    } else {
      this.mediaInfo = { ...context.media, type: "basic" };
      this.mediaInfo.isAudioOnly = isAudioOnlyMediaType(mediaType);
    }
    this.qualityTracker = context.qualityTracker;
    this.applyInitialPlaybackState(context.initialPlaybackState);
    this.mediaRangeObservable = htmlMediaRangeObservable(context.mediaElement);
    this.addDestroyable(subscribe(this.mediaRangeObservable, (mediaRange) => this.updateMediaRange(mediaRange)));
  }
  static canPlayType(mediaType) {
    return canPlayType(mediaType);
  }
  static getAdapterInfo() {
    return adapterInfo;
  }
  getAdapterInfo() {
    return adapterInfo;
  }
  getMediaInfo() {
    return this.mediaInfo;
  }
  applyInitialPlaybackState(state) {
    const { playbackRate, mute, volume, startTime, startTimeLive } = state ?? {};
    if (playbackRate !== void 0) {
      this.setPlaybackRate(playbackRate);
    }
    if (mute === true) {
      this.mute();
    } else if (mute === false) {
      this.unmute();
    }
    if (volume !== void 0) {
      this.setVolume(volume);
    }
    this.startTime = startTime;
    this.startTimeLive = startTimeLive;
  }
  async internalPreload(preloadDestroyables, options) {
    const htmlMediaElementPreloadValue = this.startTimeLive !== void 0 ? "auto" : options?.loadData !== void 0 ? options.loadData ? "auto" : "metadata" : void 0;
    logger2.debug(`Preload attribute: ${htmlMediaElementPreloadValue}`);
    if (htmlMediaElementPreloadValue !== void 0) {
      this.mediaElement.preload = htmlMediaElementPreloadValue;
    }
    const abortController = new DestroyableAbortController();
    preloadDestroyables.addDestroyable(abortController);
    let firstMediaRange;
    let loader;
    let loaderAborted = false;
    const firstMediaRangeObserver = subscribe(this.mediaRangeObservable, (mediaRange) => {
      logger2.log("First media range:", mediaRange);
      firstMediaRange = mediaRange;
      firstMediaRangeObserver.destroy();
    });
    preloadDestroyables.addDestroyable(firstMediaRangeObserver);
    if (this.mediaInfo.type === "hls") {
      const mediaInfo = this.mediaInfo;
      loader = mediaInfo.loadMasterPlaylist({ signal: abortController.signal }).then(() => {
        this.createSubtitleControls(mediaInfo.masterPlaylist?.subtitles);
        this.configureQualityTracker(mediaInfo.qualities, mediaInfo.isAudioOnly);
        logger2.log("Master playlist loaded.");
      }).then(() => whenDestroyed(firstMediaRangeObserver)).then(() => {
        if (firstMediaRange === void 0) {
          loaderAborted = true;
          throw new AbortError("Media range observation was aborted.");
        }
        if (firstMediaRange.complete) {
          logger2.log("On-demand HLS stream.");
          return this.applyStartTime();
        } else {
          logger2.log("Live HLS stream.");
          const mediaRange = { ...firstMediaRange };
          return mediaInfo.loadMediaPlaylist({ signal: abortController.signal }).then(() => this.setHlsProgramDateTime(mediaRange, mediaInfo)).then(() => this.applyStartTime());
        }
      }).catch((e) => {
        if (isAbortError(e)) {
          loaderAborted = true;
          return;
        }
        const error = PlaybackSessionError.fromUnknownError(e, this.playbackStarted, "Failed to load HLS playlists.");
        this.handleError(error);
      });
    } else {
      loader = whenDestroyed(firstMediaRangeObserver).then(() => {
        if (firstMediaRange === void 0) {
          loaderAborted = true;
        } else {
          return this.applyStartTime();
        }
      });
    }
    logger2.debug(`Setting src to: ${this.mediaInfo.url}`);
    this.mediaElement.src = this.mediaInfo.url;
    await loader;
    if (loaderAborted) {
      throw new AbortError("Preload was aborted.");
    }
  }
  setHlsProgramDateTime(mediaRange, mediaInfo) {
    const programDateTime = mediaInfo.programDateTime ?? this.guessHlsProgramDateTime(mediaInfo.playlistDuration, mediaInfo.averageSegmentDuration);
    if (!Number.isFinite(mediaRange.end)) {
      const artificialDuration = Math.max(0, mediaInfo.playlistDuration - 3 * mediaInfo.averageSegmentDuration);
      logger2.log("Patching media range.", {
        seekableEnd: mediaRange.end,
        playlistDuration: mediaInfo.playlistDuration,
        usingRangeEnd: artificialDuration
      });
      mediaRange.end = artificialDuration;
    }
    this.handleMediaRangeUpdate(mediaRange, void 0, programDateTime);
  }
  /**
   * Send a fresh media range or program date/time to the time super class.
   * Normalize a little and resolve the live position for live streams (it's
   * ignored for on-demand media).
   */
  updateMediaRange(range, programDateTime) {
    range.start = Math.max(0, range.start);
    range.end = Math.max(range.start, range.end);
    const averageSegmentDuration = this.mediaInfo.type === "hls" ? this.mediaInfo.averageSegmentDuration : 10;
    const livePosition = Number.isFinite(range.end) ? Math.max(0, range.end - 3 * averageSegmentDuration) : void 0;
    this.handleMediaRangeUpdate(range, livePosition, programDateTime);
  }
  createSubtitleControls(subtitles) {
    if (this.subtitleControls !== void 0 || !subtitles?.length) {
      return;
    }
    logger2.log("Creating controls for media provided subtitles:", subtitles.map((s) => s.label).join(", "));
    this.subtitleControls = new HlsNativeSubtitles(this.mediaElement, this.eventEmitter, subtitles);
    this.subtitleControls.setDefaultSubtitles();
  }
  /**
   * Quality tracking applies to videos played on a video element when
   * qualities are known (variable bitrate streams). Quality tracking is based
   * on polling the video dimensions, as HTML5 doesn't provide other means, so
   * audio quality can't be tracked.
   */
  configureQualityTracker(qualities, isAudioOnly) {
    if (qualities === void 0 || isAudioOnly || !(this.mediaElement instanceof HTMLVideoElement)) {
      return;
    }
    logger2.log("Setting up quality tracking (polling) for video.");
    this.qualityTracker.setAvailableQualities(qualities);
    const isNotUndefined = (value) => value !== void 0;
    const frameRates = Array.from(new Set(qualities.map((q) => q.frameRate).filter(isNotUndefined)));
    this.addDestroyable(
      subscribe(
        htmlVideoQualityObservable(this.mediaElement, { frameRates }),
        (quality) => this.qualityTracker.setCurrentQualityByVideoProps(quality.width, quality.height, quality.frameRate)
      )
    );
  }
  /**
   * Apply start time. The method will return before the seek is applied or the
   * seek is completed if it determines that a call to play() is required for
   * further progress, in order to not create a deadlock. The seek and seeked
   * logic will still be applied even if the method returns early, just not
   * waited for.
   */
  async applyStartTime() {
    const mediaElement = this.mediaElement;
    const mediaInfo = this.mediaInfo;
    const startTimeLiveApplies = mediaInfo.type === "hls" && mediaInfo.mediaPlaylist?.fixedEnd === false;
    const startTimeApplies = mediaInfo.type !== "hls" || mediaInfo.mediaPlaylist?.fixedStart !== false;
    const holdWhileBuffering = mediaInfo.type === "hls" && mediaInfo.mediaPlaylist?.fixedEnd === false;
    logger2.debug("Start time data:", {
      startTimeLiveApplies,
      startTimeApplies,
      startTimeLive: this.startTimeLive ?? "n/a",
      startTime: this.startTime ?? "n/a",
      holdWhileBuffering
    });
    let startTime;
    if (startTimeLiveApplies && this.startTimeLive !== void 0) {
      startTime = this.liveTimeToPosition(this.startTimeLive);
    } else if (startTimeApplies && this.startTime !== void 0) {
      startTime = this.startTime;
    } else {
      return;
    }
    if (startTimeLiveApplies && startTimeApplies && startTime === 0) {
      startTime = 0.01;
    }
    const waitForLoadedMetadata = async () => {
      const destroyable = addEventListener2(mediaElement, "loadedmetadata", () => {
        destroyable.destroy();
      });
      this.addDestroyable(destroyable);
      await whenDestroyed(destroyable);
      if (mediaElement.readyState < mediaElement.HAVE_METADATA) {
        throw new AbortError("Aborted.");
      }
      logger2.log("Got loadedmetadata");
    };
    const waitForLoadedData = async () => {
      const destroyable = addEventListener2(mediaElement, "loadeddata", () => {
        destroyable.destroy();
      });
      this.addDestroyable(destroyable);
      await whenDestroyed(destroyable);
      if (mediaElement.readyState < mediaElement.HAVE_CURRENT_DATA) {
        throw new AbortError("Aborted.");
      }
      logger2.log("Got loadeddata");
    };
    const doneOrRequirePlay = new DestroyableSet();
    const checkIfRequirePlay = async () => {
      if (!doneOrRequirePlay.destroyed && mediaElement.networkState !== mediaElement.NETWORK_LOADING) {
        logger2.log("Media not loading. Play required to continue start time seeking.");
        doneOrRequirePlay.destroy();
        return waitForLoadedData();
      }
    };
    const seek = async () => {
      const seeking = new DestroyableSet(this);
      seeking.addDestroyables([
        addEventListener2(mediaElement, "seeking", () => seeking.destroy()),
        addEventListener2(mediaElement, "progress", () => {
          logger2.log(`Calling seekTo: ${startTime}`);
          this.seekTo(startTime);
        })
      ]);
      logger2.log(`Calling seekTo: ${startTime}`);
      this.seekTo(startTime);
      await whenDestroyed(seeking);
    };
    if (holdWhileBuffering) {
      const minBuffer = 3 * mediaInfo.averageSegmentDuration;
      let previousBufferAmount = -1;
      let repeatBufferEvents = 0;
      doneOrRequirePlay.addDestroyable(
        addEventListener2(mediaElement, "progress", () => {
          const buffer = convertTimeRanges(mediaElement.buffered);
          const bufferRange = buffer.find((range) => range.start - 1 <= startTime && range.end >= startTime);
          const bufferLength = bufferRange !== void 0 ? bufferRange.end - bufferRange.start : 0;
          logger2.log(`Buffered media at startTime: ${bufferLength}s`);
          if (bufferLength < minBuffer) {
            if (bufferLength === previousBufferAmount) {
              if (++repeatBufferEvents === 2) {
                logger2.log("Got 3 progress events with equal buffer length. Letting it continue...");
                doneOrRequirePlay.destroy();
              }
            } else {
              previousBufferAmount = bufferLength;
              repeatBufferEvents = 0;
            }
            return;
          }
          logger2.log("Seek target sufficiently buffered");
          doneOrRequirePlay.destroy();
        })
      );
    } else {
      doneOrRequirePlay.addDestroyables([
        addEventListener2(mediaElement, "seeked", () => {
          logger2.log("Seeked to start time:", mediaElement.currentTime);
          doneOrRequirePlay.destroy();
        }),
        addEventListener2(mediaElement, "progress", () => {
          logger2.log("Progress:", {
            currentTime: mediaElement.currentTime,
            duration: mediaElement.duration,
            readyState: mediaElement.readyState,
            networkState: mediaElement.networkState
          });
          if (mediaElement.readyState >= mediaElement.HAVE_FUTURE_DATA) {
            error = new Error("Got future or enough data but no seeked event. Browser is broken.");
            doneOrRequirePlay.destroy();
          }
        })
      ]);
    }
    let error;
    Promise.resolve().then(() => checkIfRequirePlay()).then(
      () => mediaElement.readyState < mediaElement.HAVE_METADATA ? waitForLoadedMetadata().then(() => checkIfRequirePlay()) : void 0
    ).then(() => seek()).then(() => checkIfRequirePlay()).catch((e) => {
      if (doneOrRequirePlay.destroyed) {
        logger2.error("Seeking to start time failed:", e);
      } else {
        error = toError(e);
        doneOrRequirePlay.destroy();
      }
    });
    await whenDestroyed(doneOrRequirePlay);
    if (error !== void 0) {
      throw error;
    }
  }
};
NativeAdapter = __decorateClass([
  implementsStatic()
], NativeAdapter);

export {
  NativeAdapter
};
